import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { useCallback, useEffect, useState } from 'react'
import { useTheme } from 'styled-components'

import { useDraggable } from '../../hooks/useDraggable'
import { Portal } from '../portal/Portal'
import { Span } from '../typography/Body'
import {
  StBottomSheetBackdrop,
  StBottomSheetCloseButton,
  StBottomSheetContainer,
  StBottomSheetContent,
  StBottomSheetContentContainer,
  StBottomSheetDraggableArea,
  StBottomSheetDraggableThumb,
} from './BottomSheet.styled'

import type { BottomSheetProps } from './BottomSheet.props'

// Utility Methods
// ------------------------------------------------------------------------- /
function getTouchPosition(event: MouseEvent | TouchEvent) {
  return (event as TouchEvent).touches ? (event as TouchEvent).touches[0] : (event as MouseEvent)
}

/**
 * Round to the closes number value in the list.
 */
function getClosest(value: number, list: number[]): number {
  return list.reduce((previous, current) =>
    Math.abs(current - value) < Math.abs(previous - value) ? current : previous,
  )
}

// Stable default so the `steps` reference doesn't change every render, which
// would otherwise re-fire the effect depending on it on each render.
const DEFAULT_STEPS: number[] = []

const BottomSheet = ({
  draggable = true,
  children,
  closeThreshold = 25,
  steps = DEFAULT_STEPS,
  visible,
  closeCtaVisible = true,
  fitContent = false,
  onClose,
}: BottomSheetProps) => {
  const theme = useTheme()

  const [notSelectable, setNotSelectable] = useState<boolean>(false)
  const [dragPosition, setDragPosition] = useState<number>()
  const [sheetHeight, setSheetHeight] = useState<number>(steps[0] ?? 0) // in vh

  // Don't render the portal at all until the sheet has been opened at least
  // once. A fixed full-viewport overlay in the DOM (even with pointer-events:none)
  // can intercept touch events on mobile WebView before the sheet is needed.
  const [hasBeenVisible, setHasBeenVisible] = useState(!!visible)
  useEffect(() => {
    if (visible) setHasBeenVisible(true)
  }, [visible])

  // Drives the open/closed CSS transition. On the first open it lags one frame
  // behind `visible`: the container must paint in its closed (translated-off,
  // aria-hidden) state before flipping open, otherwise it mounts already-open
  // and pops in without animating. Once mounted it stays in the DOM, so later
  // opens already have a closed starting frame and animate without the delay.
  const [open, setOpen] = useState(!!visible)
  useEffect(() => {
    if (!visible || !hasBeenVisible) {
      setOpen(false)
      return
    }
    const frame = requestAnimationFrame(() => setOpen(true))
    return () => cancelAnimationFrame(frame)
  }, [visible, hasBeenVisible])

  useEffect(() => {
    if (visible) {
      // Reset the state to initial when the sheet becomes visible
      setSheetHeight(steps[0] ?? 0)
    }
  }, [visible, steps])

  // Draggable Methods
  // ------------------------------------------------------------------------- /
  const onDragStart = useCallback(
    (event: MouseEvent | TouchEvent) => {
      setDragPosition(getTouchPosition(event).pageY)
      setNotSelectable(true)
    },
    [setDragPosition, setNotSelectable],
  )

  const onDragMove = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!dragPosition) {
        return
      }

      const y = getTouchPosition(event).pageY
      const deltaY = dragPosition - y
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight
      const deltaHeight = (deltaY / viewportHeight) * 100

      setSheetHeight((currentValue) => currentValue + deltaHeight)
      setDragPosition(y)
    },
    [dragPosition, setSheetHeight, setDragPosition],
  )

  const onDragEnd = useCallback(() => {
    setDragPosition(undefined)
    setNotSelectable(false)

    if (sheetHeight < closeThreshold) {
      if (onClose) {
        onClose()
      }
    } else if (steps.length > 0) {
      // Round sheetHeight to the closest step
      setSheetHeight(getClosest(sheetHeight, steps))
    }
  }, [setDragPosition, setNotSelectable, closeThreshold, sheetHeight, steps, onClose])

  const { handleRef } = useDraggable<HTMLDivElement>({
    onDragStart,
    onDragMove,
    onDragEnd,
  })

  // Register the portal container on first render so #bottom-sheet-container
  // is appended to document.body in the correct DOM order (before #modal-container).
  // Only render the sheet content after first open to avoid a fixed full-viewport
  // overlay that would stack above the modal and hide it.
  if (!hasBeenVisible) {
    return <Portal wrapperId='bottom-sheet-container'>{null}</Portal>
  }

  return (
    <Portal wrapperId='bottom-sheet-container'>
      <StBottomSheetContainer aria-hidden={!open}>
        <StBottomSheetBackdrop onClick={onClose} />

        <StBottomSheetContentContainer
          notSelectable={notSelectable}
          draggable={draggable}
          fitContent={fitContent}
          // Only drive the height inline when fixed vh steps are provided.
          // Without steps (and without fitContent) fall back to the CSS default
          // height so the sheet doesn't collapse to a zero-height sliver.
          style={
            fitContent || steps.length === 0
              ? undefined
              : {
                  height: `calc(${sheetHeight}vh - var(--safe-area-inset-top))`,
                }
          }
        >
          {draggable ? (
            <header className='controls'>
              <StBottomSheetDraggableArea ref={handleRef}>
                <StBottomSheetDraggableThumb />
              </StBottomSheetDraggableArea>

              {closeCtaVisible ? (
                <StBottomSheetCloseButton onClick={onClose}>
                  <Span type='BodyMediumBold' color={theme.Colors.Neutrals.Darkest} style={{ fontSize: 16 }}>
                    {t({ id: 'globals.close', message: 'Sluiten' })}
                  </Span>
                  <FontAwesomeIcon icon={['fal', 'times']} fontSize={16} color={theme.Colors.Theme.Primary} />
                </StBottomSheetCloseButton>
              ) : null}
            </header>
          ) : null}

          <StBottomSheetContent draggable={draggable} fitContent={fitContent}>
            {children}
          </StBottomSheetContent>
        </StBottomSheetContentContainer>
      </StBottomSheetContainer>
    </Portal>
  )
}

export { BottomSheet }
